import { useRef } from "react";
import useSearchStore from "@/stores/useSearchStore";
import { ComboBoxItem } from "@/utils/helper.util";
import { buildSearchRequestBody } from "@/utils/searchRequestBuilder.util";
import { LocationAutocompleteEntryDto } from "@advanced-sales-technologies/backend-client/models/LocationAutocompleteEntryDto";
import { useInfiniteQuery } from "@tanstack/react-query";

import { useGetOrgaInfo } from "@/hooks/api/organisation/useGetOrgaInfo";
import useSalesyApiClient from "@/hooks/useSalesyApiClient";
import { PredefinedFoundingDateRange } from "@/components/organisms/filter/FoundingDateFilter";

export type Range = { min?: number; max?: number };

export type LocationOption = ComboBoxItem & LocationAutocompleteEntryDto;

export type SearchParams = {
  companyInclude: ComboBoxItem[];
  companyExclude: ComboBoxItem[];
  showTerminated: boolean;
  locationInclude: LocationOption[];
  locationExclude: LocationOption[];
  industryInclude: ComboBoxItem[];
  industryExclude: ComboBoxItem[];
  advancedIndustryInclude: ComboBoxItem[];
  advancedIndustryIncludeMainIds: string[];
  advancedIndustryExclude: ComboBoxItem[];
  advancedIndustryExcludeMainIds: string[];
  headcount: Range[];
  headcountFree: { min?: string; max?: string };
  revenue: Range[];
  revenueFree: { min?: string; max?: string };
  revenueGrowth: Range[];
  revenueGrowthFree: { min?: string; max?: string };
  foundingDate: { min?: string; max?: string };
  predefinedFoundingDate: { id: string; value: PredefinedFoundingDateRange }[] | null;
  keywords: string[];
  keywordsExclude: string[];
  hq: boolean;
  limit?: number;
  offset?: number;
};

export const searchQueryKey = "search";

export const useInfiniteSearch = (searchParams: SearchParams) => {
  const { apiClient } = useSalesyApiClient();
  const itemsTotalRef = useRef<number | null>(null);
  const { data: organisation } = useGetOrgaInfo();
  const submitted = useSearchStore((state) => state.submitted);

  const search = async ({ pageParam = 0 }) => {
    const baseQuery = buildSearchRequestBody(searchParams);

    const response = await apiClient.companies.search({
      requestBody: {
        filter: baseQuery,
        page: pageParam,
        items_per_page: searchParams?.limit || 25,
      },
    });

    if (pageParam === 0 && response.info?.items_total) {
      itemsTotalRef.current = response.info.items_total;
    }

    return response;
  };

  return useInfiniteQuery({
    queryKey: [searchQueryKey, searchParams],
    queryFn: search,
    enabled: !!searchParams && submitted,
    initialPageParam: 0,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
    getNextPageParam: (lastPage) => {
      const { info } = lastPage || {};
      if (!info) return undefined;

      const itemsTotal = info?.items_total ?? itemsTotalRef.current;
      if (!itemsTotal) return undefined;

      const maxItems = Math.min(itemsTotal, organisation?.plan?.limit || 25);
      const maxPage = Math.ceil(maxItems / info.items_per_page);

      return info.page < maxPage - 1 ? info.page + 1 : undefined;
    },
  });
};
